import React, { useState } from 'react';
import useAuth from '../../hooks/utils/useAuth';
import { useBackNavigation } from '../../hooks/utils/useNavigation';
import { useMediaQuery } from '../../hooks/utils/useMediaQuery';
import { Header } from '../../components/layout/header';
import { RedirectHeader } from '../../components/layout/redirect-header';
import { resolveTheme } from '../../themes';
import { getFaqPageCopy } from './common/faq-copy';

const FAQPage: React.FC = () => {
  const navigateBack = useBackNavigation();
  const { user } = useAuth();
  const dfMdMedia = useMediaQuery('md');
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const { subtitle, items: faqData } = getFaqPageCopy(resolveTheme().id);

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const handleBackClick = () => {
    navigateBack();
  };

  return (
    <div className="bg-landing-off-white min-h-screen">
      {/* Header: regular when auth'd, redirect when not */}
      {user ? <Header /> : <RedirectHeader />}

      {/* Content below header (offset for fixed Header on mobile when auth'd) */}
      <div className={!dfMdMedia && user ? 'pt-[76px]' : ''}>
        {/* Back button below header */}
        <div className="w-full px-8 pt-6 lg:px-24">
          <button
            onClick={handleBackClick}
            className="hover:text-primary flex items-center gap-2 text-black transition-colors duration-200"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-['Montserrat'] text-sm font-medium">Back</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="mx-auto max-w-4xl px-8 pb-12 pt-6 lg:px-24">
          {/* Page Title */}
          <div className="mb-12 text-center">
            <h1 className="leading-12 lg:leading-14 font-['Manrope'] text-4xl font-light text-black lg:text-5xl">
              Frequently Asked Questions
            </h1>
            <p className="text-placeholder-gray mt-4 font-['Montserrat'] text-lg font-normal leading-7">{subtitle}</p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqData.map((item, index) => (
              <div
                key={index}
                className="border-input-border-gray rounded-lg border bg-white transition-all duration-200 hover:shadow-sm"
              >
                <button onClick={() => toggleItem(index)} className="w-full rounded-lg px-6 py-6 text-left">
                  <div className="flex items-center justify-between">
                    <h3 className="pr-4 font-['Manrope'] text-lg font-medium leading-7 text-black">{item.question}</h3>
                    <div className="flex-shrink-0">
                      <svg
                        className={`text-placeholder-gray h-5 w-5 transition-transform duration-200 ${
                          openItems.has(index) ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </button>

                {openItems.has(index) && (
                  <div className="px-6 pb-6">
                    <div className="border-input-border-gray border-t pt-4">
                      <p className="whitespace-pre-line font-['Montserrat'] text-base font-normal leading-7 text-gray-700">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-16 text-center">
            <p className="text-placeholder-gray font-['Montserrat'] text-sm font-normal">
              Still have questions?{' '}
              <a
                href="mailto:questions@kindlyhuman.com"
                className="text-section-header-slate hover:text-slate-hover transition-colors duration-200"
              >
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
